import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Plus, X, Loader2, Link2, Github, Search } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Textarea } from "@/components/uiDir/textarea";
import { Label } from "@/components/uiDir/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/uiDir/select";
import { Badge } from "@/components/uiDir/badge";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { Switch } from "@radix-ui/react-switch";
import { Collaborator, NewCollaborator } from "@/lib/models/addprojectModel";

export default function AddProject() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [projectData, setProjectData] = useState({
    title: "",
    description: "",
    status: "completed",
    category: "",
    startDate: "",
    endDate: "",
    liveUrl: "",
    githubUrl: "",
    showOpportunities: false,
    opportunities: [] as string[],
    collaborators: [] as Collaborator[],
    tags: [] as string[],
    
  });
  const [newRole, setNewRole] = useState("");
  const categories = [
    { value: "web_development", label: "Web Development" },
    { value: "mobile_app", label: "Mobile App" },
    { value: "ui_ux_design", label: "UI/UX Design" },
    { value: "graphic_design", label: "Graphic Design" },
    { value: "film_video", label: "Film/Video" },
    { value: "3d_modeling", label: "3D Modeling" },
    { value: "animation", label: "Animation" },
    { value: "photography", label: "Photography" },
    { value: "game_development", label: "Game Development" },
    { value: "other", label: "Other" },
  ];

    
const [searchUsername, setSearchUsername] = useState("");
const [searchResults, setSearchResults] = useState<Collaborator[]>([]);
const [collaborators, setCollaborators] = useState<NewCollaborator[]>([]);
const [newCollaborator, setNewCollaborator] = useState<NewCollaborator>({
  id: "",
  username: "",
  name: "",
  email: "",
  role: "",
});

// Search users by username
const handleSearchChange = async (value: string) => {
  setSearchUsername(value);
  if (value.trim().length < 2) {
    setSearchResults([]);
    return;
  }

  const { data, error } = await supabase
    .from("profiles")
    .select("id, username, full_name, email_public")
    .ilike("username", `%${value}%`)
    .limit(5);

  if (error) {
    console.error("Error fetching users:", error);
    return;
  }

  setSearchResults(data || []);
};

// Select collaborator from search
const handleSelectUser = (user: Collaborator) => {
  setNewCollaborator({
    id: user.id,
    username: user.username,
    name: user.full_name || user.username,
    email: user.email_public || "",
    role: "",
  });
  setSearchResults([]);
  setSearchUsername(user.username);
};

// Add collaborator to list
const handleAddCollaborator = () => {
  if (!newCollaborator.id || !newCollaborator.role.trim()) {
    toast.error("Select a user and assign a role");
    return;
  }

  setCollaborators([...collaborators, newCollaborator]);
  setNewCollaborator({ id: "", username: "", name: "", email: "", role: "" });
  setSearchUsername("");
};

// Remove collaborator
const handleRemoveCollaborator = (index: number) => {
  setCollaborators(collaborators.filter((_, i) => i !== index));
};



  const handleAddTag = () => {
    if (newTag.trim() && tags.length < 10 && !tags.includes(newTag.trim().toLowerCase())) {
      setTags([...tags, newTag.trim().toLowerCase()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      
      if (!isValidType) {
        toast.error(`${file.name} is not a valid image or video file`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Max size is 10MB`);
        return false;
      }
      return true;
    });

    if (uploadedFiles.length + validFiles.length > 10) {
      toast.error('Maximum 10 files allowed');
      return;
    }

    setUploadedFiles([...uploadedFiles, ...validFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const uploadProjectMedia = async (projectId: string) => {
  const uploadedUrls: { url: string; isCover: boolean }[] = [];

  for (let i = 0; i < uploadedFiles.length; i++) {
    const file = uploadedFiles[i];
    const fileExt = file.name.split('.').pop();
    const filePath = `${projectId}/${Date.now()}_${i}.${fileExt}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('project-media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('project-media')
        .getPublicUrl(filePath);

      if (!publicUrlData?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      uploadedUrls.push({
        url: publicUrlData.publicUrl,
        isCover: i === 0, 
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(`Failed to upload ${file.name}`);
    }
  }

  return uploadedUrls;
};


  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!projectData.title.trim()) {
    toast.error('Project title is required');
    return;
  }

  if (!projectData.description.trim()) {
    toast.error('Project description is required');
    return;
  }

  if (!projectData.category) {
    toast.error('Please select a category');
    return;
  }

  if (uploadedFiles.length === 0) {
    toast.error('Please upload at least one image or video');
    return;
  }

  setIsLoading(true);

  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error('You must be logged in to create a project');
      navigate('/login');
      return;
    }

    const { data: project, error: projectError } = await supabase
      .from('projects')
      .insert({
        user_id: user.id,
        title: projectData.title.trim(),
        description: projectData.description.trim(),
        status: projectData.status,
        category: projectData.category,
        start_date: projectData.startDate || null,
        end_date: projectData.endDate || null,
        live_url: projectData.liveUrl || null,
        github_url: projectData.githubUrl || null,
        opportunities: projectData.showOpportunities ? projectData.opportunities : [],
      })
      .select()
      .single();

    if (projectError) throw projectError;

 
    const mediaUrls = await uploadProjectMedia(project.id);

    if (mediaUrls.length > 0) {
      const mediaRecords = mediaUrls.map(media => ({
        project_id: project.id,
        file_url: media.url,
        file_type: media.url.match(/\.(mp4|mov|avi|mkv)$/i) ? 'video' : 'image',
        is_cover: media.isCover,
      }));

      const { error: mediaInsertError } = await supabase
        .from('project_media')
        .insert(mediaRecords);

      if (mediaInsertError) {
        console.error('Error inserting media records:', mediaInsertError);
        toast.error('Failed to save media information');
      }
    }

    // Insert tags
    if (tags.length > 0) {
      const tagInserts = tags.map(tag => ({
        project_id: project.id,
        tag: tag
      }));

      const { error: tagsError } = await supabase
        .from('project_tags')
        .insert(tagInserts);

      if (tagsError) {
        console.error('Error inserting tags:', tagsError);
      }
    }

    // Insert collaborators
    if (collaborators.length > 0) {
      const collaboratorInserts = collaborators.map((col, index) => ({
        project_id: project.id,
        collaborator_id: col.id,
        collaborator_name: col.name,
        collaborator_email: col.email || null,
        role: col.role,
        display_order: index + 1,
      }));

      const { error: collaboratorError } = await supabase
        .from("project_collaborators")
        .insert(collaboratorInserts);

      if (collaboratorError) {
        console.error("Error inserting collaborators:", collaboratorError);
        toast.error("Failed to save collaborators");
      }
    }

    toast.success('Project created successfully!');

    setTimeout(() => {
      navigate('/dashboard');
    }, 1000);

  } catch (err) {
    console.error('Error creating project:', err);
    toast.error(err.message || 'Failed to create project');
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-4xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-2">Add New Project</h1>
          <p className="text-gray-600">Showcase your amazing work to the world</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="shadow-xl border-0s">
            <CardHeader>
              <CardTitle>Project Media</CardTitle>
              <CardDescription>Upload images or videos (max 10 files, 10MB each)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-purple-600 transition-colors cursor-pointer relative">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isLoading}
                />
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop files here, or click to browse
                </p>
                <p className="text-xs text-gray-500">
                  Supported: JPG, PNG, WebP, MP4, MOV
                </p>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        {file.type.startsWith('image/') ? (
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <p className="text-xs text-gray-600">Video</p>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      {index === 0 && (
                        <Badge className="absolute bottom-2 left-2 text-xs">Cover</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Project Details */}
          <Card className="shadow-xl border-0">
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  placeholder="Enter project title"
                  value={projectData.title}
                  onChange={(e) =>
                    setProjectData({ ...projectData, title: e.target.value })
                  }
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your project..."
                  value={projectData.description}
                  onChange={(e) =>
                    setProjectData({ ...projectData, description: e.target.value })
                  }
                  rows={6}
                  maxLength={5000}
                  disabled={isLoading}
                  required
                />
                <p className="text-xs text-gray-500 text-right">
                  {projectData.description.length}/5000 characters
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <Select
                    value={projectData.status}
                    onValueChange={(value) =>
                      setProjectData({ ...projectData, status: value })
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="concept">Concept</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={projectData.category}
                    onValueChange={(value) =>
                      setProjectData({ ...projectData, category: value })
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={projectData.startDate}
                    onChange={(e) =>
                      setProjectData({ ...projectData, startDate: e.target.value })
                    }
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={projectData.endDate}
                    onChange={(e) =>
                      setProjectData({ ...projectData, endDate: e.target.value })
                    }
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Opportunities */}
              {(projectData.status === "in_progress" || projectData.status === "concept") && (
                <div className="pt-4 border-t mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="opportunity-switch">Add Opportunity  v</Label>
                    <Switch
                      id="opportunity-switch"
                      checked={projectData.showOpportunities}
                      onCheckedChange={(checked) =>
                        setProjectData({ ...projectData, showOpportunities: checked })
                      }
                    />
                  </div>

                  {projectData.showOpportunities && (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g. Web Developer, 3D Artist"
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          disabled={isLoading}
                        />
                        <Button
                          variant="default"
                          onClick={() => {
                            if (!newRole.trim()) return;
                            setProjectData({
                              ...projectData,
                              opportunities: [
                                ...(projectData.opportunities || []),
                                newRole.trim(),
                              ],
                            });
                            setNewRole("");
                          }}
                          disabled={isLoading}
                        >
                          Add
                        </Button>
                      </div>

                      {projectData.opportunities?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {projectData.opportunities.map((role, index) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="flex items-center gap-1"
                            >
                              {role}
                              <button
                                onClick={() =>
                                  setProjectData({
                                    ...projectData,
                                    opportunities: projectData.opportunities.filter(
                                      (_, i) => i !== index,
                                    ),
                                  })
                                }
                                className="ml-1 text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>



          {/* Tags */}
          <Card className="shadow-xl border-0">
            <CardHeader>
              <CardTitle>Tags</CardTitle>
              <CardDescription>Add relevant tags (max 10)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="px-3 py-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(index)}
                      className="ml-2 hover:text-red-600"
                      disabled={isLoading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  disabled={tags.length >= 10 || isLoading}
                />
                <Button 
                  type="button" 
                  onClick={handleAddTag} 
                  disabled={tags.length >= 10 || isLoading}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Collaborators Section */}
          <Card className="shadow-xl border-0">
            <CardHeader>
              <CardTitle>Collaborators</CardTitle>
              <CardDescription>Search and tag collaborators by username</CardDescription>
            </CardHeader>

            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="flex-1 relative">
                  <Label htmlFor="collaborator-username">Username</Label>
                  <div className="flex items-center relative">
                    <Search className="absolute left-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="collaborator-username"
                      placeholder="Search username..."
                      className="pl-8"
                      value={searchUsername}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                  </div>

                  {searchResults.length > 0 && (
                    <div className="absolute z-10 bg-white border rounded-md mt-1 w-full shadow-md max-h-48 overflow-y-auto">
                      {searchResults.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleSelectUser(user)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                        >
                          <p className="font-medium">{user.full_name || user.username}</p>
                          <p className="text-xs text-gray-500">{user.username}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <Label htmlFor="collaborator-role">Role</Label>
                  <Input
                    id="collaborator-role"
                    placeholder="Designer, Developer, etc."
                    value={newCollaborator.role}
                    onChange={(e) =>
                      setNewCollaborator({ ...newCollaborator, role: e.target.value })
                    }
                    disabled={!newCollaborator.id}
                  />
                </div>

                <div className="flex items-end">
                  <Button type="button" onClick={handleAddCollaborator} disabled={!newCollaborator.id}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {collaborators.length > 0 && (
                <ul className="space-y-2">
                  {collaborators.map((col, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center p-3 border rounded-md"
                    >
                      <div>
                        <p className="font-medium">{col.name}</p>
                        <p className="text-sm text-gray-500">
                          {col.role} — @{col.username}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveCollaborator(index)}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>


          {/* External Links */}
          <Card className="shadow-xl border-0">
            <CardHeader>
              <CardTitle>External Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="liveUrl">
                  <Link2 className="inline h-4 w-4 mr-2" />
                  Live Demo URL
                </Label>
                <Input
                  id="liveUrl"
                  type="url"
                  placeholder="https://example.com"
                  value={projectData.liveUrl}
                  onChange={(e) => setProjectData({ ...projectData, liveUrl: e.target.value })}
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="githubUrl">
                  <Github className="inline h-4 w-4 mr-2" />
                  GitHub Repository
                </Label>
                <Input
                  id="githubUrl"
                  type="url"
                  placeholder="https://github.com/username/repo"
                  value={projectData.githubUrl}
                  onChange={(e) => setProjectData({ ...projectData, githubUrl: e.target.value })}
                  disabled={isLoading}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4">
            <Link to="/dashboard">
              <Button type="button" variant="outline" disabled={isLoading}>
                Cancel
              </Button>
            </Link>
            <Button 
              type="submit" 
              disabled={isLoading} 
              className="bg-primary hover:bg-primary/90 text-white flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Project"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}